import { MobileDataTable, WSBlotterTableMaxRecordsReachedWarning, type MobileDataTableProps } from '@talos/kyoko';
import { useMemo, type ComponentProps } from 'react';
import {
  getPositionsBlotterLastUpdateTime,
  getPositionsBlotterRowAsset,
  getPositionsBlotterRowKey,
  type PositionsBlotterRow,
} from '../../../../Blotters/PositionsV3/positionBlotterRow';
import { MobilePositionCard, useMobilePositionCardLayout } from './MobilePositionCard';
import { MobilePositionGroupCard } from './MobilePositionGroupCard';
import { renderPositionWarningDetails } from './mobilePositionWarnings';

export type { PositionsBlotterRow };

export const getPositionKey = getPositionsBlotterRowKey;

export const getPositionAsset = getPositionsBlotterRowAsset;

type MobilePositionsBlotterViewProps<TRow extends PositionsBlotterRow> = {
  blotterTable: Omit<
    MobileDataTableProps<TRow>,
    'cardRenderer' | 'groupCardRenderer' | 'cardHeight' | 'cardRendererProps' | 'detailsTitle'
  > & {
    /** Present for WebSocket blotters (`useWsBlotterTable`); absent for observable-based tables. */
    paginationLimit?: Omit<ComponentProps<typeof WSBlotterTableMaxRecordsReachedWarning<TRow>>, 'getTimestamp'>;
  };
};

/**
 * Shared render shell for every mobile positions sub-blotter variant.
 *
 * Owns the parts every variant repeats — the max-records-reached warning
 * and the `MobileDataTable` configuration (card renderer, group renderer,
 * card height, details title) — so per-variant components only have to wire
 * their own data pipeline and hand back the result of `useWsBlotterTable` /
 * `useBlotterTable`.
 */
export function MobilePositionsBlotterView<TRow extends PositionsBlotterRow>({
  blotterTable,
}: MobilePositionsBlotterViewProps<TRow>) {
  const { paginationLimit, ...mobileDataTableProps } = blotterTable;
  const { cardHeight, cardRendererProps, compact } = useMobilePositionCardLayout();
  const gridOptions = useMemo(
    () => ({
      ...mobileDataTableProps.gridOptions,
      ...(compact ? { groupHideParentOfSingleChild: false } : {}),
    }),
    [compact, mobileDataTableProps.gridOptions]
  );

  return (
    <>
      {paginationLimit ? (
        <WSBlotterTableMaxRecordsReachedWarning<TRow>
          {...paginationLimit}
          getTimestamp={getPositionsBlotterLastUpdateTime}
        />
      ) : null}
      <MobileDataTable<TRow>
        {...mobileDataTableProps}
        gridOptions={gridOptions}
        cardRenderer={MobilePositionCard}
        cardRendererProps={cardRendererProps}
        groupCardRenderer={MobilePositionGroupCard}
        cardHeight={cardHeight}
        detailsTitle="Position Details"
        rowDetailHeader={renderPositionWarningDetails}
      />
    </>
  );
}
